import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { toDataSourceRequestString } from '@progress/kendo-data-query';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Dimension } from 'src/app/_models/dimension';
import { DimensionData } from 'src/app/_models/dimensionData';
import { environment } from 'src/environments/environment';

export interface OtherGlEnabledJournalCheck {
   exists: boolean;
   dimensionName: string;
}

@Injectable({
   providedIn: 'root',
})
export class DimensionService {
   baseUrl = environment.apiUrl;
   dimensionSelected = new Subject<Dimension>();
   constructor(private http: HttpClient) { }
   changeDimension(d: Dimension) {
      this.dimensionSelected.next(d);
   }

   getDimensionsFull(): Observable<Dimension[]> {
      return this.http.get<Dimension[]>(this.baseUrl + 'dimension/full');
   }
   getActiveDimensionsFull(): Observable<Dimension[]> {
      return this.http.get<Dimension[]>(this.baseUrl + 'dimension/active');
   }
   getDimensionsFullWithData(): Observable<Dimension[]> {
      return this.http.get<Dimension[]>(this.baseUrl + 'dimension/full/data');
   }
   getDimensionsForGrouping(): Observable<Dimension[]> {
      return this.http.get<Dimension[]>(this.baseUrl + 'dimension/group');
   }
   getDimension(id): Observable<Dimension> {
      return this.http.get<Dimension>(this.baseUrl + 'dimension/' + id);
   }
   updateDimension(id: number, dimension: Dimension) {
      return this.http.put(this.baseUrl + 'dimension/' + id, dimension);
   }
   createDimension(dimension: Dimension) {
      return this.http.post(this.baseUrl + 'dimension', dimension);
   }
   deleteDimension(id: string) {
      return this.http.delete(this.baseUrl + 'dimension/' + id);
   }

   getDimensionData(id): Observable<DimensionData> {
      return this.http.get<DimensionData>(this.baseUrl + 'dimension/data/' + id);
   }
   getDimensionDataForAssetPerDimension(assetCode: number, dimensionId: number): Observable<DimensionData> {
      return this.http.get<DimensionData>(this.baseUrl + 'dimension/data/asset/' + dimensionId + '/' + assetCode);
   }
   getDimensionDataListForDimension(id): Observable<DimensionData[]> {
      return this.http.get<DimensionData[]>(this.baseUrl + 'dimension/data/list/' + id);
   }
   updateDimensionData(id: number, dimension: DimensionData) {
      return this.http.put(this.baseUrl + 'dimension/data/' + id, dimension);
   }
   createDimensionData(dimension: DimensionData) {
      return this.http.post(this.baseUrl + 'dimension/data', dimension);
   }
   deleteDimensionData(id: number) {
      return this.http.delete(this.baseUrl + 'dimension/data/' + id);
   }

   getDimensionDataForGrid(dimensionId: number, state: any): Observable<GridDataResult> {
      const queryStr = `${toDataSourceRequestString(state)}`; // Serialize the state

      const url = `${this.baseUrl}${'dimension/data/' + dimensionId}?${queryStr}`;

      return this.http.get(url).pipe(
         map(
            (
               { data, total /*, aggregateResults*/ }: GridDataResult // Process the response
            ) =>
               <GridDataResult>{
                  // If there are groups, convert them to a compatible format
                  data: data,
                  total: total,
               }
         )
      );
   }

   getDimensionDataByDimensionCode(dimensionCode: string): Observable<DimensionData[]> {
      return this.http.get<DimensionData[]>(this.baseUrl + 'dimension/dataByDimensionCode/list/' + dimensionCode);
   }

   export(OnlyTemplate: boolean): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      return this.http.get(this.baseUrl + 'dimension/export/' + OnlyTemplate, {
         responseType: 'blob'
      });
   }

   hasJournalsForAccount(accountId: string): Observable<boolean> {
      return this.http.get<boolean>(this.baseUrl + 'dimension/journal/exists', {
         params: {
            accountId: accountId || ''
         }
      });
   }

   hasJournalsForDimensionAccounts(dimensionId: number): Observable<boolean> {
      return this.http.get<boolean>(this.baseUrl + 'dimension/journal/existsfordimension/' + dimensionId);
   }

   hasJournalsForOtherGlEnabledDimensionAccounts(dimensionId: number): Observable<OtherGlEnabledJournalCheck> {
      return this.http.get<OtherGlEnabledJournalCheck>(this.baseUrl + 'dimension/journal/existsforotherglenabled/' + dimensionId);
   }
}
